import React from "react";
import { Box, CircularProgress } from "@mui/material";
import ThreadConnector from "@/assets/icons/agents-targets/threaded-targets/targets-thread";
import AvatarIcon from "@/assets/icons/agents-targets/icons/AvatarIcon";
import PlusExpandIcon from "@/assets/icons/agents-targets/icons/PlusExpandIcon";
import MinusCollapseIcon from "@/assets/icons/agents-targets/icons/MinusCollapseIcon";
import { ThreadConnectorProps } from "../types";
import {
  calculateDynamicPositions,
  calculateChildrenPositions,
  calculateThreadHeight,
} from "../utils";
import TargetCard from "@/modules/targetsv2/components/target-card";

const ThreadConnectorNode: React.FC<ThreadConnectorProps> = ({
  top,
  username,
  hasChildren = false,
  children = [],
  isExpanded = false,
  onExpandClick,
  expandedStates,
  onChildExpandClick,
  parentHovered = false,
  hoveredStates,
  onMouseEnter,
  onMouseLeave,
  isLoading = false,
  loadingNodes = new Set(),
  nodeCognitoIds = new Map(),
  targetsData,
  user,
  quarter,
  year,
}) => {
  // Check if this user has dual targets (both team and personal with non-zero values)
  const hasDualTargets = React.useMemo(() => {
    if (!targetsData || targetsData.length === 0) return false;
    const hasTeam = targetsData.some(
      (d) =>
        d.targetType === "team" && parseFloat(d.target.replace(/,/g, "")) > 0
    );
    const hasPersonal = targetsData.some(
      (d) =>
        d.targetType === "personal" &&
        (parseFloat(d.target.replace(/,/g, "")) > 0 ||
          parseFloat(d.current.replace(/,/g, "")) > 0)
    );
    return hasTeam && hasPersonal;
  }, [targetsData]);

  const threadHeight = calculateThreadHeight(
    children,
    expandedStates,
    isExpanded,
    hasDualTargets
  );

  const dynamicChildren = isExpanded
    ? calculateDynamicPositions(
        calculateChildrenPositions(children, hasDualTargets),
        expandedStates
      )
    : [];
  const isHovered = hoveredStates[username];

  return (
    <Box
      sx={{
        position: "absolute",
        left: "0px",
        top: `${top}px`,
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        transition: "top 0.3s ease-in-out",
      }}
    >
      {/* Connector SVG */}
      <Box sx={{ display: "inline-block" }}>
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 0V0C1 6.62742 6.37258 12 13 12V12"
            stroke={parentHovered ? "#015C9A" : "#DCE4E9"}
          />
        </svg>
      </Box>

      {/* Avatar and username */}
      <Box
        sx={{
          display: "flex",
          alignItems: "start",
          mt: 1,
          position: "relative",
        }}
        onMouseEnter={(event) => {
          event.stopPropagation();
          onMouseEnter(username);
        }}
        onMouseOver={(event) => {
          event.stopPropagation();
          onMouseEnter(username);
        }}
        onMouseLeave={() => onMouseLeave(username)}
      >
        <AvatarIcon width={32} height={32} />
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "start",
          }}
        >
          <TargetCard
            name={username}
            targetsData={targetsData}
            user={user}
            quarter={quarter}
            year={year}
          />
        </Box>

        {/* Child thread and expand/collapse controls */}
        {hasChildren && (
          <Box
            sx={{
              position: "absolute",
              left: "16px",
              top: 32,
              zIndex: 1,
            }}
          >
            {/* Child thread */}
            <Box
              sx={{
                display: "inline-block",
                "& svg path": {
                  transition: "stroke 0.2s ease-in-out",
                },
                "&:hover svg path": {
                  stroke: "#015C9A",
                },
              }}
            >
              <ThreadConnector
                height={threadHeight}
                stroke={isHovered ? "#015C9A" : "#DCE4E9"}
                showConnector={!isExpanded || children.length === 0}
              />
            </Box>

            {/* Expand/Collapse icon */}
            <Box
              sx={{
                position: "absolute",
                left: isExpanded ? "-15px" : "0px",
                top: isExpanded ? "20px" : `${threadHeight - 13}px`,
                zIndex: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              }}
              onClick={onExpandClick}
            >
              <Box sx={{ ml: 1 }}>
                {isLoading ? (
                  <CircularProgress size={16} />
                ) : isExpanded ? (
                  <MinusCollapseIcon width={16} height={16} />
                ) : (
                  <PlusExpandIcon width={16} height={16} />
                )}
              </Box>
            </Box>

            {/* Nested children */}
            <Box
              sx={{
                opacity: isExpanded ? 1 : 0,
                visibility: isExpanded ? "visible" : "hidden",
                transition:
                  "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
              }}
            >
              {dynamicChildren.map((child, index) => (
                <ThreadConnectorNode
                  key={`${child.username}-${index}`}
                  top={child.position}
                  username={child.username}
                  hasChildren={child.hasChildren}
                  children={child.children}
                  isExpanded={expandedStates[child.username]}
                  onExpandClick={() => onChildExpandClick(child.username)}
                  expandedStates={expandedStates}
                  onChildExpandClick={onChildExpandClick}
                  parentHovered={isHovered}
                  hoveredStates={hoveredStates}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  isLoading={loadingNodes.has(
                    nodeCognitoIds.get(child.username) || ""
                  )}
                  loadingNodes={loadingNodes}
                  nodeCognitoIds={nodeCognitoIds}
                  targetsData={child.targetsData}
                  user={user}
                  quarter={quarter}
                  year={year}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ThreadConnectorNode;
